import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';

function includesAll(target: readonly IObjectType[], from: readonly IObjectType[]): boolean {
  return from.every((e) => target.includes(e));
}

function includesAny(target: readonly IObjectType[], from: readonly IObjectType[]): boolean {
  return from.some((e) => target.includes(e));
}

function canCreate(objectType: IObjectType, creatable: IObjectType[]): boolean {
  if (objectType.abstract && !includesAny(creatable, objectType.subtypes)) {
    return false;
  }
  if (!includesAll(creatable, objectType.masters)) {
    return false;
  }
  const supertypeMasters = objectType.indirectSupertypes.flatMap((s) => s.masters.slice());
  if (!includesAll(creatable, supertypeMasters)) {
    return false;
  }
  return true;
}

function removeCreatable(objectTypes: readonly IObjectType[]): IObjectType[] {
  // Create a copy of objectTypes
  const result = objectTypes.slice();
  const creatable: IObjectType[] = [];
  let added = true;
  while (added && result.length !== 0) {
    added = false;
    // Iterate backwards because array is reindexed when we remove an element
    for (let i = result.length - 1; i >= 0; --i) {
      const objectType = result[i];
      if (canCreate(objectType, creatable)) {
        creatable.push(objectType);
        added = true;
        result.splice(i, 1);
      }
    }
  }
  return result;
}

/**
 * Check if the model has no dependency loops.
 */
class DependencyLoopCheck extends Check {
  static description = 'Check for dependency loops';

  check(): Report {
    this.report = new Report();
    const notCreatable = removeCreatable(this.model.objectTypes);

    if (notCreatable.length === 0) {
      this.report.addMessage(
        MessageType.INFORMATION,
        'All objects can successfully be created.',
        this,
        this.element
      );
    } else {
      for (const objectType of notCreatable) {
        if (objectType.abstract) {
          this.report.addMessage(
            MessageType.ERROR,
            `Abstract object ${objectType.asString()} can never be created.`,
            this,
            objectType
          );
        } else {
          this.report.addMessage(
            MessageType.ERROR,
            `Instances of ${objectType.asString()} can never be created because of a dependency or inheritance loop.`,
            this,
            objectType
          );
        }
      }
    }

    return this.report;
  }
}

export default DependencyLoopCheck;
